import React from 'react';
import { translate } from 'react-i18next';
import { Button, Alert } from 'react-bootstrap';
import { FormControl } from 'react-bootstrap';
import DataTable from '../datatable/DataTable';
import PaginateSelect  from '../field_group/PaginateSelect';

import './RelationDataTable.css';

/*
The component displays list of relationship object, can do action as follows
 - List relationship object
 - Create relationship object
 - Edit relationship object
 - Delete relationship object
 - Search dependent object
 - Create dependent object then insert as selected dependent object in creation form(last line)
*/
export class RelationDataTable extends React.Component {
  constructor(props) {
    super(props);

    const attr = props.columns
      .filter(item => item.attr && item.attr.isRequired)
      .reduce((acc, item) => ({ ...acc, [typeof item.accessor === 'function' ? item.id : item.accessor]: undefined }), {});

    this.state = {
      isShowEditInline: undefined,
      isShowDeleteConfirm: false,
      isShowErrorMessage: false,
      create: {
        selectedOption: null,
        ...attr,
      },
      edit: undefined,
      delete: undefined,
      rowIndex: undefined,
    };
    this.indexSelected= undefined;
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.relationship.isCreating !== nextProps.relationship.isCreating && !nextProps.relationship.isCreating ) {
      const initialCreate = Object.keys(this.state.create).reduce((acc, key) => ({...acc, [key]: null}), {});
      this.setState({ create: initialCreate });
    }
    if (this.props.relationship.isEditing !== nextProps.relationship.isEditing && !nextProps.relationship.isEditing ) {
      this.resetEditInline();
    }
    if (this.props.relationship.isDeleting !== nextProps.relationship.isDeleting && !nextProps.relationship.isDeleting) {
      this.setState({ isShowDeleteConfirm: false });
    }

    if (this.props.errorMessage !== nextProps.errorMessage) {
      // if error message disappeared, hide error message
      // if new there is new error message, show error message
      this.setState({ isShowErrorMessage: !!nextProps.errorMessage });
    }
  }

  prepareColumns(columns) {
    const { isCreating, isEditing } = this.props.relationship;
    const { create, edit, isShowEditInline } = this.state;
    const { t } = this.props;
    return columns.map(column => {
      let Footer = undefined;
      let Cell = column.Cell;
      if (column.entity) {
        Footer = (
          <PaginateSelect
            value={create.selectedOption}
            placeholder={`${t('select')}...`}
            isDisabled={isCreating}
            cacheOptions
            defaultoptions
            classNamePrefix={this.props.classNamePrefix}
            menuPlacement="top"
            loadOptions={(value, page) => column.entity.list(value, page)}
            onChange={option => this.setState({ create: { ...this.state.create, selectedOption: option } })}
          />
        );
      } else if (column.attr) {
        const { type, props } = column.attr;
        const name = typeof column.accessor === 'function' ? column.id : column.accessor;
        Footer = (
          <FormControl
            {...props}
            value={create[name] || ''}
            disabled={isCreating}
            type={type || 'text'}
            onChange={({ target: { validity, value } }) =>
              validity.valid && this.setState({ create: { ...this.state.create, [name]: value } })}
          />
        );

        Cell = (cellProps) => {
          const { original, index: rowIndex } = cellProps;
          if(rowIndex === isShowEditInline) this.indexSelected= rowIndex;
          return rowIndex === isShowEditInline
           ? (
            (column.renderEditCell ? (column.renderEditCell(cellProps, (value) => {
              this.setState({edit: {...this.state.edit, [name]: value }})
            }, edit[name] || '')) :
            <FormControl
              {...props}
              value={edit[name] !== null ? edit[name] : ''}
              disabled={isEditing}
              type={type || 'text'}
              onChange={event => this.setState({edit: {...this.state.edit, [name]: event.target.value}})}
            />
           )
          )
           : (column.Cell ? column.Cell(cellProps) : original[name]);
        }
      }
      return { ...column, Footer, Cell, style: { ...column.style, overflow: 'visible' } };
    });
  }

  createRelation() {
    const { create } = this.props.relationship;
    const {
      selectedOption: { value: entityId, label: name, original },
      ...attrs
    } = this.state.create;
    if (create) {
      create({
        entityId,
        name,
        original,
        ...attrs,
      });
      //// for clear selected field
      this.setState({ create: {
        selectedOption: null,
      },})
    }
  }

  editInline({ original, index }) {
    this.setState({
      isShowEditInline: index,
      edit: original,
    });
  }

  resetEditInline() {
    this.setState({
      isShowEditInline: undefined,
      edit: undefined,
    });
  }

  renderAddButton() {
    const { selectedOption, ...attrs } = this.state.create;
    const { isCreating } = this.props.relationship;
    const { t } = this.props;

    const requireFieldsValid = Object.values(attrs).every(attr => !!attr);
    const disabled = !selectedOption || !requireFieldsValid || isCreating;
    return (
      <Button bsStyle="primary"
        style={{ width: '100%' }}
        disabled={disabled}
        onClick={() => this.createRelation()}
      >
        {isCreating ? `${t('adding')}...` : t('add')}
      </Button>
    );
  }

  renderDeleteConfirm() {
    const { delete: relationship,rowIndex:index} = this.state;
    const { isDeleting } = this.props.relationship;
    const { t, relationshipNameAccessor } = this.props;
    return (
      <Alert
        bsStyle="danger"
        onDismiss={() => this.setState({ isShowDeleteConfirm: false })}
      >
        <p>{t('do you want to delete')} {relationshipNameAccessor(relationship)}?</p>
        <p>
          <Button
            onClick={() => {
              this.props.relationship.delete(relationship.id?relationship.id:index||index>=0, relationship)
              if (index >= 0) {
                this.setState({ isShowDeleteConfirm: false });
              }
            }}
            bsStyle="danger"
            disabled={isDeleting}
          >
            {isDeleting ? `${t('deleting')}...` : t('delete')}
          </Button>
          <span> {t('or')} </span>
          <Button
            onClick={() => this.setState({ isShowDeleteConfirm: false })}
            disabled={isDeleting}
          >
            {t('hide')}
          </Button>
        </p>
      </Alert>
    );
  }

  renderErrorMessage(errorMessage) {
    if (!errorMessage) return null;
    return (
      <Alert
        bsStyle="danger"
        style={{ 'white-space': 'pre' }}
        onDismiss={() => this.setState({ isShowErrorMessage: false })}
      >
        {errorMessage}
      </Alert>
    );
  }

  render() {
    const {
      columns,
      data,
      filteredCount,
      isLoading,
      onFetchData,
      onActionCustom,
      customActionIcon,
      shouldShowActionMenu,
      errorMessage,
    } = this.props;
    const fetchDataAttr = onFetchData ? { onFetchData } : undefined;
    const customActionAttr = onActionCustom ? { onActionCustom, customActionIcon, shouldShowActionMenu } : undefined;
    let onActionEdit = item => this.editInline(item);
    let onActionDelete = item => this.setState({ isShowDeleteConfirm: true, delete: item.original, rowIndex:item.index });
    return (
      <div>
        {this.state.isShowErrorMessage ? this.renderErrorMessage(errorMessage) : null}
        {this.state.isShowDeleteConfirm ? this.renderDeleteConfirm() : null}
        <DataTable
          columns={this.prepareColumns(columns)}
          data={data}
          onActionEdit={this.props.relationship.edit && onActionEdit}
          onActionDelete={this.props.relationship.delete && onActionDelete}
          onActionSave={() => this.props.relationship.edit(this.state.edit,this.indexSelected)}
          onActionCancel={() => this.resetEditInline()}
          filteredCount={filteredCount || data.length}
          isLoading={isLoading}
          actionFooter={this.renderAddButton()}
          materials = {this.props.materials}
          {...fetchDataAttr}
          {...customActionAttr}
        />
      </div>
    );
  }
}

RelationDataTable.defaultProps = {
  columns: [],
  data: [],
  errorMessage: '',
  relationship: {
    list: null,
    create: null,
    edit: null,
    delete: null,
    isCreating: false,
    isDeleting: false,
    isEditing: false,
  },
  relationshipNameAccessor: item => item.name,
};

export default translate('translations')(RelationDataTable);



// WEBPACK FOOTER //
// ./src/components/datatable/RelationDataTable.js